/**
 * PWA install banner.
 *
 * Captures the `beforeinstallprompt` event (Chrome/Edge/Android) and lets the
 * user trigger installation from a discrete button. On iOS (which doesn't
 * expose the event), shows a brief instructions hint instead.
 *
 * Hides itself permanently once the user installs or dismisses (localStorage).
 */
import React, { useEffect, useState } from 'react';
import { DeviceMobile, X, ArrowDown } from '@phosphor-icons/react';

const DISMISS_KEY = 'sg-pwa-install-dismissed';

const isStandalone = () => {
    if (typeof window === 'undefined') return false;
    return (
        window.matchMedia?.('(display-mode: standalone)').matches
        || window.navigator.standalone === true
    );
};

const isIos = () => {
    if (typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent || '';
    return /iPhone|iPad|iPod/i.test(ua) && !/CriOS|FxiOS|EdgiOS/i.test(ua);
};

const PWAInstallPrompt = () => {
    const [deferred, setDeferred] = useState(null);
    const [dismissed, setDismissed] = useState(() => {
        try { return localStorage.getItem(DISMISS_KEY) === '1'; } catch (_) { return false; }
    });
    const [installed, setInstalled] = useState(isStandalone());
    const ios = isIos();

    useEffect(() => {
        const onBip = (e) => {
            e.preventDefault();
            setDeferred(e);
        };
        const onInstalled = () => {
            setInstalled(true);
            setDeferred(null);
        };
        window.addEventListener('beforeinstallprompt', onBip);
        window.addEventListener('appinstalled', onInstalled);
        return () => {
            window.removeEventListener('beforeinstallprompt', onBip);
            window.removeEventListener('appinstalled', onInstalled);
        };
    }, []);

    if (installed || dismissed) return null;
    // On non-iOS, only show when we have a deferred prompt event
    if (!ios && !deferred) return null;

    const dismiss = () => {
        try { localStorage.setItem(DISMISS_KEY, '1'); } catch (_) { /* noop */ }
        setDismissed(true);
    };

    const install = async () => {
        if (!deferred) return;
        try {
            deferred.prompt();
            const choice = await deferred.userChoice;
            if (choice?.outcome === 'accepted') setInstalled(true);
        } catch (_) { /* user cancelled or unsupported */ }
        setDeferred(null);
    };

    return (
        <div
            data-testid="pwa-install-prompt"
            className="mx-3 mb-3 border p-3 relative"
            style={{
                background: 'linear-gradient(135deg, rgba(34,197,94,0.08), rgba(0,0,0,0.4))',
                borderColor: 'rgba(34,197,94,0.4)',
                borderRadius: 2,
            }}
        >
            <button
                onClick={dismiss}
                data-testid="pwa-install-dismiss"
                title="Zavřít"
                className="absolute top-1.5 right-1.5 p-1 opacity-60 hover:opacity-100"
            >
                <X size={11} weight="bold" />
            </button>
            <div className="flex items-center gap-2 mb-2">
                <DeviceMobile size={16} weight="duotone" color="#22c55e" />
                <span className="text-[10px] uppercase tracking-[0.15em] font-bold"
                      style={{ color: '#22c55e' }}>
                    Nainstalovat jako appku
                </span>
            </div>
            {ios && !deferred ? (
                <div className="text-[11px] leading-snug" style={{ color: '#cbd5e1' }}>
                    Pro instalaci na iPhone: <span style={{ color: '#22c55e' }}>Sdílet</span> <ArrowDown size={11} className="inline" weight="bold" /> →
                    <span style={{ color: '#22c55e' }}> Přidat na plochu</span>.
                </div>
            ) : (
                <button
                    onClick={install}
                    data-testid="pwa-install-btn"
                    className="w-full text-xs font-bold uppercase tracking-[0.1em] py-1.5 transition-colors"
                    style={{
                        background: '#22c55e',
                        color: '#0B0E14',
                        borderRadius: 2,
                    }}
                >
                    Přidat na plochu
                </button>
            )}
        </div>
    );
};

export default PWAInstallPrompt;
